/**
 * useWebhooks — W2.5
 *
 * Verwaltet Webhook-Konfigurationen: Liste, Create, Delete, Test-Delivery.
 * Endpoints:
 *   GET    /api/v2/webhooks
 *   POST   /api/v2/webhooks
 *   DELETE /api/v2/webhooks/:id
 *   POST   /api/v2/webhooks/:id/test
 */
import { useState, useCallback } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import apiClient from '../../lib/api-client'

// --- Typen ---

export type WebhookConfig = {
  id: number
  url: string
  event_filter: string[]
  active: boolean
  consecutive_failures: number
  created_at: string
}

export type WebhookConfigWithSecret = WebhookConfig & {
  secret: string
}

export type WebhookTestResult = {
  success: boolean
  http_status: number | null
  response_body: string | null
  duration_ms: number | null
  error?: string
}

export type CreateWebhookParams = {
  url: string
  event_filter?: string[]
  secret?: string
}

type WebhooksListResponse = {
  data: WebhookConfig[]
}

type WebhookCreateResponse = {
  data: WebhookConfigWithSecret
}

type WebhookTestResponse = {
  data: WebhookTestResult
}

// --- Event-Katalog ---

export const WEBHOOK_EVENTS = [
  'reward.received',
  'wallet.linked',
  'wallet.unlinked',
  'hotspot.status_changed',
  'subscription.activated',
  'subscription.cancelled',
] as const

// i18n-Keys (relativ zum 'developer'-Namespace) statt fest übersetztem Text —
// die rendernde Komponente löst sie via t(EVENT_LABEL_KEYS[event]) auf, damit
// ein Sprachwechsel reaktiv wirkt (siehe developer.json webhooks.eventLabels.*).
export const EVENT_LABEL_KEYS: Record<string, string> = {
  'reward.received':        'webhooks.eventLabels.reward',
  'wallet.linked':          'webhooks.eventLabels.walletLinked',
  'wallet.unlinked':        'webhooks.eventLabels.walletUnlinked',
  'hotspot.status_changed': 'webhooks.eventLabels.hotspotStatusChanged',
  'subscription.activated': 'webhooks.eventLabels.subscriptionActivated',
  'subscription.cancelled': 'webhooks.eventLabels.subscriptionCancelled',
}

// --- Hook ---

type UseWebhooksResult = {
  webhooks: WebhookConfig[]
  isLoading: boolean
  isError: boolean
  isPremiumError: boolean
  refetch: () => void
  createWebhook: (params: CreateWebhookParams) => Promise<WebhookConfigWithSecret>
  deleteWebhook: (id: number) => Promise<void>
  testWebhook: (id: number) => Promise<WebhookTestResult>
  isCreating: boolean
  deletingId: number | null
  testingId: number | null
}

export function useWebhooks(): UseWebhooksResult {
  const queryClient = useQueryClient()
  const [isCreating, setIsCreating] = useState(false)
  const [deletingId, setDeletingId] = useState<number | null>(null)
  const [testingId, setTestingId] = useState<number | null>(null)

  const query = useQuery<WebhookConfig[], { status?: number }>({
    queryKey: ['webhooks'],
    queryFn: async () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const client = apiClient as any
      const result = await client.GET('/api/v2/webhooks', {})
      if (result.error) {
        const err = result.error as { status?: number }
        throw err
      }
      return (result.data as WebhooksListResponse).data
    },
  })

  const isPremiumError =
    query.isError &&
    !!query.error &&
    (query.error as { status?: number }).status === 402

  const createWebhook = useCallback(async (params: CreateWebhookParams): Promise<WebhookConfigWithSecret> => {
    setIsCreating(true)
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const client = apiClient as any
      const body: Record<string, unknown> = { url: params.url }
      if (params.event_filter && params.event_filter.length > 0) {
        body.event_filter = params.event_filter
      }
      if (params.secret) {
        body.secret = params.secret
      }
      const result = await client.POST('/api/v2/webhooks', { body })
      if (result.error) {
        throw result.error
      }
      const created = (result.data as WebhookCreateResponse).data
      // Zur Liste hinzufügen (ohne secret)
      const listItem: WebhookConfig = {
        id: created.id,
        url: created.url,
        event_filter: created.event_filter,
        active: created.active,
        consecutive_failures: created.consecutive_failures,
        created_at: created.created_at,
      }
      queryClient.setQueryData<WebhookConfig[]>(['webhooks'], (prev = []) => [listItem, ...prev])
      return created
    } finally {
      setIsCreating(false)
    }
  }, [queryClient])

  const deleteWebhook = useCallback(async (id: number): Promise<void> => {
    setDeletingId(id)
    const previous = queryClient.getQueryData<WebhookConfig[]>(['webhooks']) ?? []
    queryClient.setQueryData<WebhookConfig[]>(['webhooks'], (prev = []) => prev.filter(w => w.id !== id))
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const client = apiClient as any
      const result = await client.DELETE(`/api/v2/webhooks/${id}`, {})
      if (result.error) {
        throw result.error
      }
    } catch (err) {
      queryClient.setQueryData<WebhookConfig[]>(['webhooks'], previous)
      throw err
    } finally {
      setDeletingId(null)
    }
  }, [queryClient])

  const testWebhook = useCallback(async (id: number): Promise<WebhookTestResult> => {
    setTestingId(id)
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const client = apiClient as any
      const result = await client.POST(`/api/v2/webhooks/${id}/test`, {})
      if (result.error) {
        throw result.error
      }
      return (result.data as WebhookTestResponse).data
    } finally {
      setTestingId(null)
    }
  }, [])

  return {
    webhooks: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError && !isPremiumError,
    isPremiumError,
    refetch: () => query.refetch(),
    createWebhook,
    deleteWebhook,
    testWebhook,
    isCreating,
    deletingId,
    testingId,
  }
}
